import React from 'react';
import { TrendingUp, BarChart2, DollarSign, Search } from 'lucide-react';

/**
 * Компонент для параметров расчета ROI и окупаемости
 */
const ROISection = ({ 
  formData, 
  handleInputChange, 
  errors
}) => {
  if (!formData.includeROICalculation) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <label className="inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            <TrendingUp className="w-5 h-5 mr-2 text-emerald-500" />
            <span>Расчет ROI и окупаемости</span>
            <input
              type="checkbox"
              name="includeROICalculation"
              checked={formData.includeROICalculation}
              onChange={handleInputChange}
              className="form-checkbox ml-3"
            />
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-emerald-500" />
          Расчет ROI и окупаемости
        </h3>
        <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            name="includeROICalculation"
            checked={formData.includeROICalculation}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2">Включить в расчет</span>
        </label>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Ожидаемый прирост эффективности */}
        <div>
          <label htmlFor="expectedEfficiencyGain" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <BarChart2 className="w-4 h-4 mr-2 text-emerald-500" />
            Ожидаемый прирост эффективности (%)
          </label>
          <div className="relative flex items-center">
            <input
              type="text"
              id="expectedEfficiencyGain"
              name="expectedEfficiencyGain"
              value={formData.expectedEfficiencyGain}
              onChange={handleInputChange}
              className={`form-input w-full pr-8 ${errors.expectedEfficiencyGain ? 'input-error' : ''}`}
              placeholder="20"
            />
            <span className="absolute right-3 text-gray-500 dark:text-gray-400">%</span>
          </div>
          {errors.expectedEfficiencyGain && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.expectedEfficiencyGain}
            </p>
          )}
        </div>

        {/* Ожидаемый прирост выручки */}
        <div>
          <label htmlFor="expectedRevenueIncrease" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-emerald-500" />
            Ожидаемый прирост выручки (%)
          </label>
          <div className="relative flex items-center">
            <input
              type="text"
              id="expectedRevenueIncrease"
              name="expectedRevenueIncrease"
              value={formData.expectedRevenueIncrease}
              onChange={handleInputChange}
              className={`form-input w-full pr-8 ${errors.expectedRevenueIncrease ? 'input-error' : ''}`}
              placeholder="10"
            />
            <span className="absolute right-3 text-gray-500 dark:text-gray-400">%</span>
          </div>
          {errors.expectedRevenueIncrease && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.expectedRevenueIncrease}
            </p>
          )}
        </div>

        {/* Текущая годовая выручка */}
        <div>
          <label htmlFor="currentAnnualRevenue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
            Текущая годовая выручка (руб.)
          </label>
          <input
            type="text"
            id="currentAnnualRevenue"
            name="currentAnnualRevenue"
            value={formData.currentAnnualRevenue}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.currentAnnualRevenue ? 'input-error' : ''}`}
            placeholder="Текущая годовая выручка"
          />
          {errors.currentAnnualRevenue && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentAnnualRevenue}
            </p>
          )}
        </div>

        {/* Текущие операционные расходы */}
        <div>
          <label htmlFor="currentOperationalCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
            Текущие операционные расходы (руб./год)
          </label>
          <input
            type="text"
            id="currentOperationalCosts"
            name="currentOperationalCosts"
            value={formData.currentOperationalCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.currentOperationalCosts ? 'input-error' : ''}`}
            placeholder="Текущие годовые расходы"
          />
          {errors.currentOperationalCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentOperationalCosts}
            </p>
          )}
        </div>
      </div>

      {/* Информация о ROI */}
      <div className="mt-4 bg-emerald-100 dark:bg-emerald-900/30 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800">
        <div className="flex items-start">
          <Search className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-1">
              Как интерпретировать ROI
            </h4>
            <p className="text-xs text-emerald-600 dark:text-emerald-400">
              ROI (Return on Investment) показывает отношение чистой прибыли к инвестициям, выраженное в процентах. 
              Положительный ROI означает, что инвестиции приносят прибыль. При сравнении альтернативных вариантов 
              трансформации, вариант с более высоким ROI обычно считается более привлекательным.
            </p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
              Срок окупаемости показывает, через какой период времени инвестиции полностью окупятся за счет дополнительной 
              прибыли, полученной в результате трансформации.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROISection;